/**
 * OtpInput — 6-box (or N-box) OTP digit entry.
 *
 * Features:
 *  - One <input> per digit, auto-advances on entry, backspace goes back
 *  - Paste: strips non-digits, fills boxes left-to-right
 *  - Numeric keyboard on mobile (inputMode="numeric")
 *  - Controlled: value is a plain string of digits ("" → "123456")
 */

import { useRef, type ClipboardEvent, type KeyboardEvent } from "react";

type OtpInputProps = {
  length?: number;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  disabled?: boolean;
};

export function OtpInput({
  length = 6,
  value,
  onChange,
  autoFocus = false,
  disabled = false,
}: OtpInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  // Pad / slice to exactly `length` characters. Note: padEnd's fill string must be non-empty —
  // padEnd(length, "") is a no-op in JS (nothing to repeat), which left short values un-padded
  // and made digits[i] undefined for empty boxes. That undefined flowed into the input's
  // `value` prop, flipping it from controlled ("") to uncontrolled (undefined) between renders
  // and triggering React's "uncontrolled input becoming controlled" warning. Padding with a
  // real space character keeps every box's value defined for the component's whole lifetime.
  const digits = value
    .replace(/\D/g, "")
    .slice(0, length)
    .padEnd(length, " ");

  function focusAt(index: number) {
    refs.current[Math.max(0, Math.min(index, length - 1))]?.focus();
  }

  function handleChange(index: number, raw: string) {
    const digit = raw.replace(/\D/g, "").slice(-1); // keep only last digit typed
    const arr = digits.split("");
    arr[index] = digit;
    const next = arr.join("").replace(/\s/g, "");
    onChange(next);
    if (digit) focusAt(index + 1);
  }

  function handleKeyDown(index: number, e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = digits.split("");
      if (arr[index] && arr[index] !== " ") {
        // Clear current digit AND step back so user can retype naturally
        arr[index] = "";
        onChange(arr.join(""));
        if (index > 0) focusAt(index - 1);
      } else if (index > 0) {
        arr[index - 1] = "";
        onChange(arr.join(""));
        focusAt(index - 1);
      }
    } else if (e.key === "Delete") {
      // Delete key: clear entire code so user can start fresh
      e.preventDefault();
      onChange("");
      focusAt(0);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      focusAt(index - 1);
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      focusAt(index + 1);
    }
  }

  function handlePaste(e: ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
    onChange(pasted.padEnd(length, "").slice(0, length));
    focusAt(Math.min(pasted.length, length - 1));
  }

  function handleFocus(index: number) {
    // Select on focus so re-entering a digit replaces immediately
    refs.current[index]?.select();
  }

  return (
    <div className="flex items-center justify-center gap-2">
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={1}
          value={digits[i] === " " ? "" : digits[i]}
          autoFocus={autoFocus && i === 0}
          disabled={disabled}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          onFocus={() => handleFocus(i)}
          autoComplete={i === 0 ? "one-time-code" : "off"}
          aria-label={`Digit ${i + 1} of ${length}`}
          className="h-13 w-11 rounded-xl border border-slate-300 bg-white text-center text-xl font-bold text-slate-900 outline-none transition
            focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100
            disabled:cursor-not-allowed disabled:opacity-50
            [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
          style={{ height: "3.25rem" }}
        />
      ))}
    </div>
  );
}
